import { firstValueFrom, map } from 'rxjs';

import { Injectable } from '@angular/core';
import { Store } from '../../../../shared/store';
import { VideoState } from './video.state';
import { Sport } from '../../models/sport';
import { VideoApi } from '../../api/video.api';
import { VideoSection } from '@kinomap-dev/ng-ui-lib';
import { BrowseSorting } from '../../models/sorting';
import { BrowseFilter } from '../../models/filter';

const videoState: VideoState = new VideoState();

@Injectable({ providedIn: 'root' })
export class VideoStore extends Store<VideoState> {
  videos$ = this.state$.pipe(map((s: VideoState) => s.videos));
  isLoadingVideos$ = this.state$.pipe(
    map((s: VideoState) => s.isLoadingVideos)
  );
  areVideosComplete$ = this.state$.pipe(
    map((s: VideoState) => s.areVideosComplete)
  );
  homeVideos$ = this.state$.pipe(map((s: VideoState) => s.homeVideos));
  videosOfTheWeek$ = this.state$.pipe(
    map((s: VideoState) => s.videosOfTheWeek)
  );
  recommendedVideos$ = this.state$.pipe(
    map((s: VideoState) => s.recommendedVideos)
  );
  top10Videos$ = this.state$.pipe(map((s: VideoState) => s.top10Videos));

  constructor(private videoApi: VideoApi) {
    super(videoState);
  }

  // SET VIDEOS

  setVideos(nextVideos: VideoSection[]): void {
    this.setState({ ...this.state, videos: nextVideos });
  }

  setIsLoadingVideos(nextIsLoadingVideos: boolean): void {
    this.setState({ ...this.state, isLoadingVideos: nextIsLoadingVideos });
  }

  setAreVideosComplete(nextAreVideosComplete: boolean): void {
    this.setState({ ...this.state, areVideosComplete: nextAreVideosComplete });
  }

  setHomeVideos(nextVideos: VideoSection[]): void {
    this.setState({ ...this.state, homeVideos: nextVideos });
  }

  setVideosOfTheWeek(nextVideosOfTheWeek: VideoSection[]): void {
    this.setState({ ...this.state, videosOfTheWeek: nextVideosOfTheWeek });
  }

  setRecommendedVideos(nextRecommendedVideos: VideoSection[]): void {
    this.setState({ ...this.state, recommendedVideos: nextRecommendedVideos });
  }

  setTop10Videos(nextTop10Videos: VideoSection[]): void {
    this.setState({ ...this.state, top10Videos: nextTop10Videos });
  }

  // GET VIDEOS FROM API

  async getVideos(
    {
      newSelectedSport,
      newFilters,
      newSorting,
      nextPage,
    }: {
      newSelectedSport?: Sport;
      newFilters?: BrowseFilter[];
      newSorting?: BrowseSorting;
      nextPage: boolean;
    } = { nextPage: false }
  ): Promise<void> {
    if (nextPage && this.state.areVideosComplete) {
      return;
    }

    if (!nextPage) {
      this.setVideos([]);
      this.setAreVideosComplete(false);
    }

    this.setIsLoadingVideos(true);

    try {
      const fetchedVideos: VideoSection[] = await firstValueFrom(
        this.videoApi.getVideos$(
          newSelectedSport,
          newFilters,
          newSorting,
          nextPage
        )
      );
      const newVideos: VideoSection[] =
        this.getVideosWithoutDuplicate(fetchedVideos);
      this.setVideos(newVideos);

      this.setAreVideosComplete(this.videoApi.isLastPageLoaded);
    } catch (err) {
      this.setAreVideosComplete(true);
    } finally {
      this.setIsLoadingVideos(false);
    }
  }

  private getVideosWithoutDuplicate(
    fetchedVideos: VideoSection[]
  ): VideoSection[] {
    const newVideosWithDuplicates: VideoSection[] = [
      ...this.state.videos,
      ...fetchedVideos,
    ];
    const obj: {} = {};
    newVideosWithDuplicates.forEach((content) => (obj[content.id] = content));
    return [
      ...new Set(
        newVideosWithDuplicates.map((video: VideoSection) => video.id)
      ),
    ].map((id: number) => obj[id]);
  }

  getAllHomeVideos(selectedSport: Sport): void {
    this.getHomeVideos(selectedSport);
    this.getVideosOfTheWeek(selectedSport);
    this.getRecommendedVideos(selectedSport);
    this.getTop10Videos(selectedSport);
  }

  private async getHomeVideos(selectedSport: Sport): Promise<void> {
    this.setHomeVideos(
      await firstValueFrom(this.videoApi.getHomeVideos$(selectedSport))
    );
  }

  private async getVideosOfTheWeek(selectedSport: Sport): Promise<any> {
    try {
      this.setVideosOfTheWeek(
        await firstValueFrom(this.videoApi.getVideosOfTheWeek$(selectedSport))
      );
    } catch (err) {
      this.setVideosOfTheWeek([]);
    }
  }

  private async getRecommendedVideos(selectedSport: Sport): Promise<void> {
    this.setRecommendedVideos(
      await firstValueFrom(this.videoApi.getRecommendedVideos$(selectedSport))
    );
  }

  private async getTop10Videos(selectedSport: Sport): Promise<void> {
    this.setTop10Videos(
      await firstValueFrom(this.videoApi.getTop10Videos$(selectedSport))
    );
  }
}
